import { type ReactElement, useEffect, useRef } from 'react';
import type { BasemapHandle, BasemapView } from './map-basemap-engine';

/**
 * D797: the platform basemap under a web map — the api's self-hosted vector style (`@vello/maps-ui` `basemapStyleUrl`, the
 * same map the native apps draw) rendered by MapLibre GL into a layer that fills its positioned parent. It only PAINTS: no
 * pointer events, no controls, nothing focusable; the map that owns it keeps its own view (centre + zoom in 256 px tiles),
 * its pins, outlines and handles, and this layer follows that view. Put it first inside the stage so everything else
 * draws over it.
 *
 * MapLibre and the PMTiles reader (~1 MB) load on first use, in their own chunk, so a screen without a map never pays for
 * them. Without WebGL (an old device, a test DOM) the layer stays empty and the stage keeps its plain ground. `onAttribution`
 * reports the data credit the style carries (OpenStreetMap), which the stage must show.
 */
export interface MapBasemapProps {
  /** The style url (`basemapStyleUrl(apiBase, scheme)`). */
  readonly styleUrl: string;
  readonly center: { readonly latitude: number; readonly longitude: number };
  /** The owning map's zoom, in 256 px tiles (`@vello/maps-ui` slippy maths); fractional zooms draw as well. */
  readonly zoom: number;
  readonly onAttribution?: (text: string) => void;
}

let gpu: boolean | undefined;

/** WebGL 2 is there (asked once per page; a test DOM never is). */
function canDraw(): boolean {
  if (gpu !== undefined) return gpu;
  if (typeof window === 'undefined' || typeof document === 'undefined' || /jsdom|happydom/i.test(window.navigator.userAgent)) {
    gpu = false;
    return gpu;
  }
  try {
    gpu = document.createElement('canvas').getContext('webgl2') !== null;
  } catch {
    gpu = false;
  }
  return gpu;
}

export function MapBasemap({ styleUrl, center, zoom, onAttribution }: MapBasemapProps): ReactElement {
  const host = useRef<HTMLDivElement | null>(null);
  const map = useRef<BasemapHandle | undefined>(undefined);
  const view = useRef<BasemapView>({ center, zoom });
  view.current = { center, zoom };
  const credit = useRef(onAttribution);
  credit.current = onAttribution;

  useEffect(() => {
    const el = host.current;
    if (el === null || !canDraw()) return undefined;
    let cancelled = false;
    let handle: BasemapHandle | undefined;
    void import('./map-basemap-engine')
      .then(({ createBasemap }) => {
        if (cancelled) return;
        handle = createBasemap(el, styleUrl, view.current, (text) => credit.current?.(text));
        map.current = handle;
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      handle?.remove();
      map.current = undefined;
    };
  }, [styleUrl]);

  const { latitude, longitude } = center;
  useEffect(() => {
    map.current?.jumpTo({ center: { latitude, longitude }, zoom });
  }, [latitude, longitude, zoom]);

  return <div ref={host} className="vello-map-basemap" aria-hidden="true" />;
}
